import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MessengerLayout from "../../layout/MessengerLayout";
import PublishEffects, { BlockingFindings }
  from "../../components/admin/workflow/PublishEffects";
// ONE BASE URL AND ONE AUTH HEADER, shared with the builder. This screen
// used to carry its own copy of both; a second copy next door would have
// been the same drift AdminModulesPage.jsx records four instances of.
import * as api from "../../components/admin/workflow/api";
import {
  Search, Filter, X, PauseCircle, FileText, CheckCircle2, PencilLine,
  AlertTriangle,
  Cpu, CircleSlash, ChevronRight, Loader2,
} from "lucide-react";

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

/*
 * Workflow Builder — the LIBRARY (brief §7 Phase 4).
 *
 * IT IS NOW THE "WORKFLOWS" TAB, and the legacy approval-chain screen
 * that used to hold that name is the "Approval chains" tab beside it.
 * Until this change two different features were both called Workflows in
 * the same admin area and the one a user landed on first was empty,
 * which reads as "the feature is broken" rather than "wrong place".
 * D2 always intended the rename; this is it.
 *
 * ONE COMPONENT, TWO MOUNTS, NOT TWO IMPLEMENTATIONS. The tab renders
 * WorkflowEngineLibraryBody directly; the /admin/workflow-engine route
 * renders the same body inside MessengerLayout so existing deep links
 * keep resolving. `embedded` controls page chrome ONLY — the heading
 * level and the outer padding, both of which the settings page already
 * supplies. Nothing about the data, the filters or the table varies.
 *
 * The tab hosts it at EXACTLY the same width: both mounts sit directly
 * inside `.kwsb-content` (margin-left 72px, padding 24px, no max-width —
 * Sidebar.jsx:621) under a `p-6`, so the table and its filter row have
 * the same space either way. The only cost of the tab is vertical: the
 * Admin Settings h1 and tab strip take about 130px above the fold.
 *
 * WHAT THIS SCREEN HAS TO COMMUNICATE, beyond listing rows: these 57
 * definitions are a REGISTRY. 56 of them document bespoke code that
 * already ships and will keep running whether or not anyone ever
 * presses Publish. If the screen implies that publishing turns a
 * workflow on, somebody will eventually believe it — so the execution
 * owner is a first-class column and the detail panel states what
 * publishing actually commits (A1).
 *
 * NO VOCABULARY IS HARDCODED HERE. Categories, modules, automation
 * modes and statuses come from GET /meta, which reads them from
 * wf_engine/models.py. AdminModulesPage.jsx is the cautionary tale — its
 * own header records that it is the second of FOUR hardcoded copies of
 * the module catalogue with nothing reconciling them.
 */

const OWNER_BADGE = {
  code: {
    icon: FileText,
    label: "Documented",
    cls: "text-sky-300 border-sky-400/40 bg-sky-400/10",
    help: "Bespoke code drives this. The definition is documentation and an observability target — the engine will never run it.",
  },
  engine: {
    icon: Cpu,
    label: "Engine-run",
    cls: "text-[#00ff90] border-[#00ff90]/40 bg-[#00ff90]/10",
    help: "The engine executes this definition.",
  },
  none: {
    icon: CircleSlash,
    label: "Not implemented",
    cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
    help: "Documented, but nothing drives it yet.",
  },
};

const prettify = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** The route mount. Deep links to /admin/workflow-engine still resolve;
 *  the canonical way in is Admin Settings → Workflows. */
export default function WorkflowEngineLibrary() {
  return (
    <MessengerLayout>
      <WorkflowEngineLibraryBody />
    </MessengerLayout>
  );
}

export function WorkflowEngineLibraryBody({ embedded = false }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    category: "", module: "", automation_mode: "", status: "",
    execution_owner: "",
  });

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const data = await api.listDefinitions(params);
      setRows(data.definitions || []);
      setTotals(data.totals || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [q, filters]);

  useEffect(() => {
    api.getMeta().then(setMeta).catch(() => {});
  }, []);

  // Debounced so typing in the search box does not fire a request per
  // keystroke against a 57-row catalogue that is filtered server-side.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    setDetailLoading(true);
    api
      .getDefinition(selected)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selected]);

  const anyFilter = useMemo(
    () => q.trim() || Object.values(filters).some(Boolean),
    [q, filters]
  );

  const clearAll = () => {
    setQ("");
    setFilters({ category: "", module: "", automation_mode: "", status: "",
      execution_owner: "" });
  };

  return (
    // EMBEDDED DROPS THE OUTER PADDING because the settings page already
    // applies p-6 inside .kwsb-content's own 24px; a second one would put
    // the table 72px off the edge while the neighbouring tabs sit at 48.
    <div className={`space-y-5 text-zinc-200 ${embedded ? "" : "p-6"}`}>
      <header className="space-y-2">
        {/* h2 in the tab: Admin Settings owns the h1, and two h1s on one
            page is a heading order a screen reader reads as two
            documents. */}
        {embedded ? (
          <h2 className="text-lg font-semibold text-[#00ff90]">
            Workflow Library
          </h2>
        ) : (
          <h1 className="text-2xl font-semibold text-[#00ff90]">
            Workflow Library
          </h1>
        )}
        <p className="max-w-3xl text-sm text-zinc-400">
          The canonical record of every HR process this platform runs.{" "}
          <span className="text-zinc-300">
            Most of these document code that already ships
          </span>{" "}
          — publishing one commits the definition and its SLA
          configuration, not the behaviour itself.{" "}
          <span className="text-zinc-500">
            Looking for approval routing — who signs off leave, expenses or a
            salary revision? That is the Approval chains tab.
          </span>
        </p>
      </header>

        {totals && (
          <div className="flex flex-wrap gap-3">
            <Stat label="Definitions" value={totals.total} />
            <Stat label="Published" value={totals.published} />
            <Stat label="Draft" value={totals.draft} />
            {/* The A3 pass, as a number somebody can watch move. It is
                counted on each definition's LATEST version, so a fresh
                draft over a reviewed version reads as pending again —
                which is the honest answer, since nobody has read the new
                content. */}
            <Stat label="Reviewed" value={totals.reviewed ?? 0} />
            <Stat
              label="Held from publish"
              value={totals.held_from_publish}
              tone="amber"
            />
          </div>
        )}

        {/* A3's review priority, stated where a reviewer starts rather
            than left in a design document. Without it people begin at
            step 1 field 1 and spend their attention on type/actor
            cosmetics. */}
        <div className="rounded-lg border border-sky-400/30 bg-sky-400/5 p-3 text-xs text-sky-100/90">
          <span className="font-medium">Reviewing these drafts?</span> Work in
          this order: <b>SLA hours and on-timeout first</b>, then approver
          strategy and notification type, then step type and actor, and trigger
          configuration last — the 12 schedule triggers are blocked on a cron
          expression nobody has read off <code>main.py</code> yet, so time spent
          there changes nothing until that happens.
        </div>

        {/* ---- search + filters ---- */}
        <div className={`${cardCls} p-4 space-y-3`}>
          <div className="flex items-center gap-2">
            <Search size={16} className="text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or summary…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-600"
            />
            {anyFilter && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[#00ff90]"
              >
                <X size={12} /> Clear
              </button>
            )}
          </div>

          {meta && (
            <div className="flex flex-wrap items-center gap-2">
              <Filter size={14} className="text-zinc-600" />
              <Select
                value={filters.category}
                onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
                placeholder="Category"
                options={meta.categories}
              />
              <Select
                value={filters.module}
                onChange={(v) => setFilters((f) => ({ ...f, module: v }))}
                placeholder="Module"
                options={meta.modules}
              />
              <Select
                value={filters.automation_mode}
                onChange={(v) =>
                  setFilters((f) => ({ ...f, automation_mode: v }))}
                placeholder="Automation"
                options={meta.automation_modes}
              />
              <Select
                value={filters.status}
                onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                placeholder="Status"
                options={meta.statuses}
              />
              <Select
                value={filters.execution_owner}
                onChange={(v) =>
                  setFilters((f) => ({ ...f, execution_owner: v }))}
                placeholder="Driven by"
                options={meta.execution_owners}
              />
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
            Could not load the library: {error}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
          {/* ---- the table ---- */}
          <div className={`${cardCls} overflow-hidden`}>
            {loading ? (
              <div className="flex items-center gap-2 p-6 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">
                Nothing matches those filters.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-[#00ff90]/20 text-left text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">#</th>
                      <th className="px-3 py-2 font-medium">Workflow</th>
                      <th className="px-3 py-2 font-medium">Category</th>
                      <th className="px-3 py-2 font-medium">Driven by</th>
                      <th className="px-3 py-2 font-medium">Automation</th>
                      <th className="px-3 py-2 font-medium">Ver</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium">Steps</th>
                      <th className="px-3 py-2 font-medium">Active</th>
                      <th className="px-3 py-2 font-medium">Review</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const badge = OWNER_BADGE[r.execution_owner] || OWNER_BADGE.none;
                      const Icon = badge.icon;
                      return (
                        <tr
                          key={r.id}
                          onClick={() => setSelected(r.id)}
                          className={`cursor-pointer border-b border-zinc-800/60 hover:bg-[#00ff90]/5 ${
                            selected === r.id ? "bg-[#00ff90]/10" : ""
                          }`}
                        >
                          <td className="px-3 py-2 text-zinc-600">{r.ref_id}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-zinc-100">{r.name}</span>
                              {r.held_from_publish && (
                                <span title={r.held_from_publish}>
                                  <PauseCircle size={13} className="text-amber-400" />
                                </span>
                              )}
                              {r.is_fork && (
                                <span className="rounded border border-violet-400/40 bg-violet-400/10 px-1 text-[10px] text-violet-300">
                                  forked
                                </span>
                              )}
                            </div>
                            <div className="truncate text-xs text-zinc-500" style={{ maxWidth: 380 }}>
                              {r.summary}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-400">
                            {prettify(r.category)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              title={badge.help}
                              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${badge.cls}`}
                            >
                              <Icon size={11} /> {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-zinc-400">
                            {prettify(r.automation_mode)}
                          </td>
                          <td className="px-3 py-2 text-zinc-400">v{r.version}</td>
                          <td className="px-3 py-2">
                            <StatusPill status={r.status} />
                          </td>
                          <td className="px-3 py-2 text-zinc-400">{r.step_count}</td>
                          <td className="px-3 py-2 text-zinc-400">
                            {r.active_instances || "—"}
                          </td>
                          <td className="px-3 py-2">
                            {r.review_state === "reviewed" ? (
                              <span
                                title={`Reviewed by ${r.reviewed_by_email || "—"}`}
                                className="inline-flex items-center gap-1 text-[11px] text-[#00ff90]"
                              >
                                <CheckCircle2 size={12} /> v{r.version}
                              </span>
                            ) : (
                              <span className="text-[11px] text-zinc-600">
                                pending
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-zinc-600">
                            <button
                              title="Open in the builder"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/admin/workflow-engine/${encodeURIComponent(r.key)}`
                                );
                              }}
                              className="rounded p-1 hover:bg-[#00ff90]/10 hover:text-[#00ff90]"
                            >
                              <ChevronRight size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ---- detail ---- */}
          <div className={`${cardCls} p-4`}>
            {!selected ? (
              <p className="text-sm text-zinc-500">
                Select a workflow to see its steps and what publishing it
                would commit.
              </p>
            ) : detailLoading ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <Loader2 size={14} className="animate-spin" /> Loading…
              </div>
            ) : !detail ? (
              <p className="text-sm text-red-300">Could not load that workflow.</p>
            ) : (
              <Detail d={detail} navigate={navigate} />
            )}
          </div>
        </div>
      </div>
  );
}

function Detail({ d, navigate }) {
  const badge = OWNER_BADGE[d.execution_owner] || OWNER_BADGE.none;
  return (
    <div className="space-y-4 text-sm">
      <div>
        <h2 className="font-semibold text-zinc-100">{d.name}</h2>
        <p className="mt-1 text-xs text-zinc-500">{d.summary}</p>
        <code className="mt-2 block text-[11px] text-zinc-600">{d.key}</code>
      </div>

      <button
        onClick={() =>
          navigate(`/admin/workflow-engine/${encodeURIComponent(d.key)}`)
        }
        className="flex w-full items-center justify-center gap-1.5 rounded border border-[#00ff90]/40 bg-[#00ff90]/10 px-3 py-1.5 text-xs text-[#00ff90] hover:bg-[#00ff90]/20"
      >
        <PencilLine size={12} /> Open in the builder
      </button>

      {d.shown_version && (
        <div className="rounded border border-zinc-800 p-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="text-zinc-400">
              Review of v{d.shown_version.version}
            </span>
            {d.shown_version.review_state === "reviewed" ? (
              <span className="inline-flex items-center gap-1 text-[#00ff90]">
                <CheckCircle2 size={12} /> Reviewed
              </span>
            ) : (
              <span className="text-zinc-500">Pending</span>
            )}
          </div>
          {d.shown_version.review_state === "reviewed" && (
            <p className="mt-1 text-[11px] text-zinc-500">
              {d.shown_version.reviewed_by_email} ·{" "}
              {(d.shown_version.reviewed_at || "").slice(0, 10)}
            </p>
          )}
        </div>
      )}

      {d.held_from_publish && (
        <Note tone="amber" icon={PauseCircle} title="Held from publish">
          {d.held_from_publish}
        </Note>
      )}

      <Note tone="sky" icon={badge.icon} title={badge.label}>
        {badge.help}
        {d.implemented_by && (
          <code className="mt-1 block text-[11px] text-zinc-400">
            {d.implemented_by}
          </code>
        )}
      </Note>

      {/* 072 — what we already know differs between this definition and
          the code it documents. Shown next to implemented_by rather than
          buried, because a reviewer about to sign off on a step list
          needs to know which parts of it the code does not do.

          073 — AND WHICH KIND OF GAP IT IS. A clarification and a
          "documented but not built" used to render identically; a
          reviewer who learns to skim the first skims the second, and the
          second is the one that matters. Vocabulary is shared with the
          builder via api.coverageKindOf so the two screens cannot end up
          describing the same gap differently. */}
      {d.coverage_note && (() => {
        const ck = api.coverageKindOf(d.coverage_kind);
        return (
          <Note tone={ck.tone} icon={AlertTriangle} title={ck.title}>
            {ck.help && (
              <p className="mb-1 opacity-80">{ck.help}</p>
            )}
            {d.coverage_note}
          </Note>
        );
      })()}

      {/* A1 — what publishing actually commits, and the publish
          blockers. Both are the SAME components the builder draws, over
          the same backend payload, so the two screens cannot end up
          describing the same act differently. */}
      <PublishEffects effects={d.validation?.publish_commits} />

      <BlockingFindings findings={d.validation?.findings} />

      {d.graph && (
        <div>
          <h3 className="mb-1 text-xs uppercase tracking-wide text-zinc-500">
            Steps ({d.graph.steps.length})
          </h3>
          <ol className="space-y-1">
            {d.graph.steps.map((s) => (
              <li
                key={s.key}
                className="flex items-start gap-2 rounded border border-zinc-800 p-2 text-xs"
              >
                <span className="text-zinc-600">{s.seq}</span>
                <span className="flex-1">
                  <span className="text-zinc-200">{s.name}</span>
                  <span className="ml-2 text-zinc-600">
                    {s.type} · {s.actor}
                  </span>
                  {s.sla_hours != null && (
                    <span className="ml-2 text-[#00ff90]">
                      SLA {s.sla_hours}h
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Note({ tone, icon: Icon, title, children }) {
  // `danger` added for coverage_kind="product_gap" (073) — a definition
  // describing behaviour that does not exist is not the same warning as
  // one whose wording is imprecise, and rendering them the same colour
  // is how a reader learns to ignore both.
  const cls =
    tone === "danger"
      ? "border-red-500/40 bg-red-500/10 text-red-200/90"
      : tone === "amber"
      ? "border-amber-500/30 bg-amber-500/5 text-amber-200/90"
      : "border-sky-400/30 bg-sky-400/5 text-sky-200/90";
  return (
    <div className={`rounded border p-2 text-xs ${cls}`}>
      <div className="mb-1 flex items-center gap-1 font-medium">
        {Icon && <Icon size={12} />} {title}
      </div>
      <div className="opacity-90">{children}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300",
    published: "border-[#00ff90]/40 bg-[#00ff90]/10 text-[#00ff90]",
    superseded: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    archived: "border-zinc-700 bg-zinc-800/40 text-zinc-500",
  };
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[11px] ${
        map[status] || map.draft
      }`}
    >
      {status}
    </span>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div
      className={`${cardCls} px-4 py-2 ${
        tone === "amber" ? "border-amber-500/30" : ""
      }`}
    >
      <div className="text-xs text-zinc-500">{label}</div>
      <div
        className={`text-lg font-semibold ${
          tone === "amber" ? "text-amber-300" : "text-[#00ff90]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function Select({ value, onChange, placeholder, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded border border-zinc-700 bg-[#0d0d0d] px-2 py-1 text-xs text-zinc-300 outline-none focus:border-[#00ff90]/50"
    >
      <option value="">{placeholder}: all</option>
      {(options || []).map((o) => (
        <option key={o} value={o}>
          {prettify(o)}
        </option>
      ))}
    </select>
  );
}
